import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ExcelDownloadButton from "../../../components/common/ExcelDownloadButton";
import { getDiscountListExportColumns } from "../../../utils/listExportColumns";
import { listAllDiscounts, archiveDiscount, activateDiscount } from "../../../api/discounts";
import Button from "../../../components/common/Button";
import Badge from "../../../components/common/Badge";
import { formatDiscountValue } from "../../../utils/currency";
import UserStatusBadge from "../../../components/common/UserStatusBadge";
import ListPageLayout, {
    ListPageHeader,
    ListFilterTabs,
    ListLoadingSpinner,
    ListEmptyState,
    ListTableCard,
} from "../../../components/layout/ListPageLayout";
import PaginationFooter from "../../../components/common/PaginationFooter";
import SortableTableHeader from "../../../components/common/SortableTableHeader";
import useSortedClientPagination from "../../../hooks/useSortedClientPagination";
import { discountListSortColumns } from "../../../config/tableSortColumns";
import { showConfirm } from '../../../utils/popup';

export default function DiscountsList() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [discounts, setDiscounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [filter, setFilter] = useState("active"); // all, active, archived

    const loadDiscounts = async () => {
        setLoading(true);
        setLoadError(false);
        try {
            const res = await listAllDiscounts();
            const list = Array.isArray(res.data) ? res.data : [];
            setDiscounts(list);
        } catch (error) {
            console.error("Failed to load discounts:", error);
            setDiscounts([]);
            setLoadError(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadDiscounts();
    }, []);

    const handleArchive = async (id) => {
        if (!await showConfirm(t("pages.discounts.archiveConfirm"))) return;
        try {
            await archiveDiscount(id);
            alert(t("pages.discounts.archiveSuccess"));
            loadDiscounts();
        } catch (error) {
            console.error("Failed to archive discount:", error);
            alert(t("pages.discounts.archiveFailed"));
        }
    };

    const handleActivate = async (id) => {
        if (!await showConfirm(t("pages.discounts.activateConfirm"))) return;
        try {
            await activateDiscount(id);
            alert(t("pages.discounts.activateSuccess"));
            loadDiscounts();
        } catch (error) {
            console.error("Failed to activate discount:", error);
            alert(t("pages.discounts.activateFailed"));
        }
    };



    const filteredDiscounts = discounts.filter(d => {
        if (filter === "active") return d.isActive === true;
        if (filter === "archived") return d.isActive === false;
        return true;
    });

    const pagination = useSortedClientPagination(filteredDiscounts, {
        columns: discountListSortColumns,
        resetKey: filter,
    });

    const getScopeDisplay = (discount) => {
        const scopes = [];
        if (discount.branchId) scopes.push(t("pages.discounts.scopeBranch"));
        if (discount.levelId) scopes.push(t("pages.discounts.scopeLevel"));
        if (discount.batchId) scopes.push(t("pages.discounts.scopeBatch"));
        return scopes.length > 0 ? scopes.join(", ") : t("pages.discounts.scopeGlobal");
    };

    const discountFilterTabs = [
        { key: 'all', label: t('common.all'), count: discounts.length },
        { key: 'active', label: t('common.active'), count: discounts.filter(d => d.isActive).length },
        { key: 'archived', label: t('common.archived'), count: discounts.filter(d => !d.isActive).length },
    ];

    return (
        <ListPageLayout>
            <ListPageHeader
                title={t("pages.discounts.title")}
                subtitle={t("pages.discounts.subtitle")}
                actions={(
                    <>
                        <ExcelDownloadButton
                            columns={getDiscountListExportColumns(t)}
                            data={filteredDiscounts}
                            filename="discounts"
                            disabled={filteredDiscounts.length === 0}
                        />
                        <button
                            onClick={() => navigate("/admin/discounts/new")}
                            className="btn-page-action-primary"
                        >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        {t("pages.discounts.create")}
                    </button>
                    </>
                )}
            />

            <ListFilterTabs tabs={discountFilterTabs} active={filter} onChange={setFilter} />

            {loading ? (
                <ListLoadingSpinner />
            ) : loadError ? (
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="status">
                    {t("pages.discounts.loadFailed")}
                </div>
            ) : filteredDiscounts.length === 0 ? (
                <ListEmptyState message={t("pages.discounts.empty")} hint={t("pages.discounts.emptyHint")} />
            ) : (
                <ListTableCard>
                    <div className="overflow-x-auto">
                        <table className="min-w-full">
                            <thead className="table-header">
                                <tr>
                                    <SortableTableHeader label={t("common.code")} columnKey="code" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t("pages.discounts.colDiscountName")} columnKey="name" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t("pages.discounts.colValue")} columnKey="value" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t("pages.discounts.colScope")} columnKey="scope" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t("common.status")} columnKey="status" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <th className="table-header-cell">{t("common.actions")}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {pagination.pageItems.map(discount => (
                                    <tr key={discount.id} className="table-row">
                                        <td className="table-cell">
                                            <div className="text-sm font-mono font-bold text-purple-600">{discount.code}</div>
                                        </td>
                                        <td className="table-cell">
                                            <div className="text-sm font-semibold text-gray-900">{discount.name}</div>
                                            {discount.description && (
                                                <div className="text-xs text-gray-500 mt-1">{discount.description}</div>
                                            )}
                                        </td>
                                        <td className="table-cell">
                                            <div className="text-sm font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                                                {formatDiscountValue(discount.discountType, discount.discountValue)}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {discount.discountType === "PERCENT" ? t("pages.discounts.typePercent") : t("pages.discounts.typeFixed")}
                                            </div>
                                        </td>
                                        <td className="table-cell">
                                            <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                                                {getScopeDisplay(discount)}
                                            </span>
                                        </td>
                                        <td className="table-cell">
                                            <UserStatusBadge active={discount.isActive} />
                                        </td>
                                        <td className="table-cell whitespace-nowrap">
                                            <div className="flex items-center justify-center gap-2">
                                                <button
                                                    onClick={() => navigate(`/admin/discounts/${discount.id}/edit`)}
                                                    className="btn-action-edit"
                                                    title={t("pages.discounts.editTitle")}
                                                >
                                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                    </svg>
                                                    {t("common.edit")}
                                                </button>
                                                {discount.isActive ? (
                                                    <button
                                                        onClick={() => handleArchive(discount.id)}
                                                        className="btn-action-archive"
                                                        title={t("pages.discounts.archiveTitle")}
                                                    >
                                                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                                                        </svg>
                                                        {t("common.archive")}
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={() => handleActivate(discount.id)}
                                                        className="btn-action-activate"
                                                        title={t("pages.discounts.activateTitle")}
                                                    >
                                                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                        {t("common.activate")}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {pagination.total > 0 && (
                        <PaginationFooter
                            page={pagination.page}
                            size={pagination.size}
                            total={pagination.total}
                            onPageChange={pagination.setPage}
                            onSizeChange={pagination.setSize}
                        />
                    )}
                </ListTableCard>
            )}
        </ListPageLayout>
    );
}
